// @file Composable that provides methods to upload a file to a comment.
import { ATTACHMENT_UPLOAD_ERROR_ALERT, INVALID_FILE_TYPE_ALERT } from '@@/bits/alert_dialog'
import { __ } from '@@/bits/intl'
import { FILE_TOO_LARGE_ERROR, startUpload, UploadJob } from '@@/bits/uploader'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { CommentAttachmentState } from '@@/pinia/surface_comment_attachments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Cid } from '@@/types'
import type { CommentCid, CommentId } from '@@/types/surface_comments'
import { getDisplayAttributes, LinkHelpers } from '@padlet/beethoven-client'
import { storeToRefs } from 'pinia'

const INVALID_FILE_TYPE_ERROR = 'INVALID_FILE_TYPE_ERROR'

class InvalidFileTypeError extends Error {
  error = INVALID_FILE_TYPE_ERROR
}

const validateFileType = (file: File): boolean => {
  if (file.type.startsWith('image/')) return true
  if (file.type.startsWith('video/')) return true
  if (file.type.startsWith('audio/')) return true
  return false
}

export const useCommentAttachmentUploader = () => {
  const { openAlertDialog } = useGlobalAlertDialogStore()

  const surfaceStore = useSurfaceStore()
  const { uploadLimit, wallId } = storeToRefs(surfaceStore)
  const { showOverFileSizeModal } = surfaceStore

  const commentsStore = useCommentsStore()
  const { startWritingNewComment, startEditingCommentV2, updateCommentDraft } = commentsStore
  const { newCommentByPostCid, draftsForEditedCommentsById, commentDraftsByCid } = storeToRefs(commentsStore)

  const surfacePostsStore = useSurfacePostsStore()
  const { postEntitiesByCid } = storeToRefs(surfacePostsStore)
  const { fetchAndStoreLinkAttributes } = useSurfaceAttachmentsStore()

  // Helper functions for cid resolution
  const getCidFromPostCid = (postCid: Cid): CommentCid => {
    let draft = newCommentByPostCid.value[postCid]
    if (!draft) {
      // Create a new draft if one doesn't exist
      const post = postEntitiesByCid.value[postCid]
      if (post?.id) {
        draft = startWritingNewComment({ postId: post.id })
      }
    }
    if (!draft) {
      throw new Error(`Could not create or find draft for postCid ${postCid}`)
    }
    return draft.cid
  }

  const getCidFromCommentId = (commentId: CommentId): CommentCid => {
    let draft = draftsForEditedCommentsById.value[commentId]
    if (!draft) {
      // Create a new draft if one doesn't exist
      draft = startEditingCommentV2({ commentId })
    }
    return draft.cid
  }

  const getDraftFromCid = (cid: CommentCid) => {
    return commentDraftsByCid.value[cid] ?? null
  }

  // #region UNIFIED CORE FUNCTIONS
  const finishPickingAttachmentForComment = async ({ cid, url }: { cid: CommentCid; url: string }): Promise<void> => {
    updateCommentDraft(cid, { attachment: url })
    const link = await fetchAndStoreLinkAttributes(url)

    // If the link is a Google Drive URL in this step, it means the Google Drive upload has failed and returned a Google Drive
    // URL instead of a upload URL. We try our best by getting the preview image URL and add it to the comment.
    if (link != null && LinkHelpers.isGoogleDriveFile(link)) {
      // What we pass to `width` here doesn't matter because we only need the original image URL.
      const displayAttrs = getDisplayAttributes(link, { width: 0 })
      const imageUrl = displayAttrs?.original_image_url ?? null
      // If at this point, an image URL is still not found, we reject the attachment.
      if (imageUrl == null) {
        throw new Error(`No image URL found for attachment ${url}`)
      }
      updateCommentDraft(cid, { attachment: imageUrl })
    }

    // If the link is not of any allowed media type, we append it to the comment body.
    else if (link == null || (!LinkHelpers.isPhoto(link) && !LinkHelpers.isVideo(link) && !LinkHelpers.isAudio(link))) {
      const draft = getDraftFromCid(cid)
      const commentBody = draft?.html_body ?? ''
      updateCommentDraft(cid, {
        html_body: `${commentBody}<p>${url}</p>`,
        attachment: null,
        attachmentState: CommentAttachmentState.IDLE,
      })
      commentsStore.runUploadStoppedHooksForDraft(cid)
      return
    }

    const draft = getDraftFromCid(cid)
    const currentState = draft?.attachmentState ?? CommentAttachmentState.IDLE
    if (currentState === CommentAttachmentState.LOADING) {
      // We only advance the state to PREVIEW if the comment is still in the LOADING state.
      // If it's not, chances are the user has already cancelled the attachment and the state
      // is now IDLE/undefined.
      updateCommentDraft(cid, { attachmentState: CommentAttachmentState.PREVIEW })
    }
    await commentsStore.runUploadCompletedHooksForDraft(cid)
  }

  const handleErrorPickingAttachmentForComment = ({
    err,
    cid,
    file,
  }: {
    err: Error & { error?: string }
    cid: CommentCid
    file?: File
  }): void => {
    const draft = getDraftFromCid(cid)

    if (draft != null) {
      // Cancel any active upload job
      draft.uploadJob?.cancel()

      // Clean up all upload-related state
      updateCommentDraft(cid, {
        attachment: null,
        attachmentState: CommentAttachmentState.IDLE,
        uploadingFile: undefined,
        uploadingProgress: undefined,
        uploadJob: undefined,
      })
    }

    if (err.error === FILE_TOO_LARGE_ERROR && file != null) {
      showOverFileSizeModal({ file })
    } else if (err.error === INVALID_FILE_TYPE_ERROR) {
      openAlertDialog(INVALID_FILE_TYPE_ALERT)
    } else {
      openAlertDialog(ATTACHMENT_UPLOAD_ERROR_ALERT)
    }

    commentsStore.runUploadStoppedHooksForDraft(cid)
  }

  const uploadFileForComment = ({
    cid,
    file,
    trimVideoAfterUpload,
  }: {
    cid: CommentCid
    file: File
    trimVideoAfterUpload?: { start: number; end: number }
  }): void => {
    if (!validateFileType(file)) {
      handleErrorPickingAttachmentForComment({
        err: new InvalidFileTypeError(`Invalid file type ${file.type}`),
        cid,
        file,
      })
      return
    }

    const draft = getDraftFromCid(cid)
    if (!draft) {
      throw new Error(`No draft found for cid ${cid}`)
    }

    // Mark the comment as being written to ensure the comment drawer shows up (for new comments)
    if (!draft.id) {
      const postId = draft.wish_id
      if (postId != null) startWritingNewComment({ postId })
    }

    updateCommentDraft(cid, { attachmentState: CommentAttachmentState.LOADING })

    const uploadJob = startUpload(file, {
      maxFileSize: uploadLimit.value,
      trimVideoAfterUpload,
      uploadPurpose: 'comment_attachment',
      wallId: wallId.value,
    })

    updateCommentDraft(cid, {
      uploadingFile: file,
      uploadJob,
      uploadingProgress: 0,
    })

    if (trimVideoAfterUpload != null) {
      showVideoTrimmingSnackbar()
    }

    uploadJob.on('progress', (progress) => {
      updateCommentDraft(cid, { uploadingProgress: progress })
    })
    uploadJob.on('done', ({ url }) => {
      void finishPickingAttachmentForComment({ cid, url })
        .then(() => {
          updateCommentDraft(cid, {
            uploadingFile: undefined,
            uploadJob: undefined,
            uploadingProgress: undefined,
          })
        })
        .catch((err) => {
          handleErrorPickingAttachmentForComment({ err, cid, file })
        })
    })
    uploadJob.on('error', (err) => {
      handleErrorPickingAttachmentForComment({ err, cid, file })
    })
  }

  const setDummyFileUploadProgressForComment = (cid: CommentCid, progress: number): void => {
    const draft = getDraftFromCid(cid)

    // If there's no active upload, set up dummy upload state
    if (!draft?.uploadingFile) {
      // For new comments, ensure the comment drawer is visible
      if (draft && !draft.id) {
        const postId = draft.wish_id
        if (postId != null) startWritingNewComment({ postId })
      }

      updateCommentDraft(cid, {
        attachmentState: CommentAttachmentState.LOADING,
        uploadingFile: new File([], 'empty.txt', { type: 'text/plain' }),
        uploadJob: new UploadJob(new File([], 'empty.txt', { type: 'text/plain' })),
      })
    }
    updateCommentDraft(cid, { uploadingProgress: progress })
  }

  const addAttachmentForComment = async ({ cid, url }: { cid: CommentCid; url: string }): Promise<void> => {
    const draft = getDraftFromCid(cid)
    if (!draft) {
      throw new Error(`No draft found for cid ${cid}`)
    }

    updateCommentDraft(cid, { attachmentState: CommentAttachmentState.LOADING })

    try {
      await finishPickingAttachmentForComment({ cid, url })
    } catch (err) {
      handleErrorPickingAttachmentForComment({ err, cid })
    }
  }
  // #endregion

  // #region NEW COMMENTS - Backward compatible wrappers

  /**
   * @deprecated Fetch the comment cid and use uploadFileForComment instead
   */
  const uploadFileForNewComment = ({
    postCid,
    file,
    trimVideoAfterUpload,
  }: {
    postCid: Cid
    file: File
    trimVideoAfterUpload?: { start: number; end: number }
  }): void => {
    const cid = getCidFromPostCid(postCid)
    uploadFileForComment({ cid, file, trimVideoAfterUpload })
  }

  /**
   * @deprecated Fetch the comment cid and use setDummyFileUploadProgressForComment instead
   */
  const setDummyFileUploadProgressForNewComment = (postCid: Cid, progress: number): void => {
    const cid = getCidFromPostCid(postCid)
    setDummyFileUploadProgressForComment(cid, progress)
  }

  /**
   * @deprecated Fetch the comment cid and use addAttachmentForComment instead
   */
  const addAttachmentForNewComment = async ({ postCid, url }: { postCid: Cid; url: string }): Promise<void> => {
    const cid = getCidFromPostCid(postCid)
    await addAttachmentForComment({ cid, url })
  }
  // #endregion

  // #region EDITED COMMENTS - Backward compatible wrappers
  /**
   * @deprecated Fetch the comment cid and use uploadFileForComment instead
   */
  const uploadFileForEditedComment = ({
    commentId,
    file,
    trimVideoAfterUpload,
  }: {
    commentId: CommentId
    file: File
    trimVideoAfterUpload?: { start: number; end: number }
  }): void => {
    const cid = getCidFromCommentId(commentId)
    uploadFileForComment({ cid, file, trimVideoAfterUpload })
  }

  /**
   * @deprecated Fetch the comment cid and use addAttachmentForComment instead
   */
  const addAttachmentForEditedComment = async ({
    commentId,
    url,
  }: {
    commentId: CommentId
    url: string
  }): Promise<void> => {
    const cid = getCidFromCommentId(commentId)
    await addAttachmentForComment({ cid, url })
  }

  /**
   * @deprecated Fetch the comment cid and use setDummyFileUploadProgressForComment instead
   */
  const setDummyFileUploadProgressForEditedComment = (commentId: CommentId, progress: number): void => {
    const cid = getCidFromCommentId(commentId)
    setDummyFileUploadProgressForComment(cid, progress)
  }
  // #endregion

  const globalSnackBarStore = useGlobalSnackbarStore()
  const showVideoTrimmingSnackbar = (): void => {
    globalSnackBarStore.setSnackbar({
      message: __('Your video is processing. This may take a few minutes.'),
    })
  }

  return {
    // Unified core functions (recommended)
    uploadFileForComment,
    finishPickingAttachmentForComment,
    handleErrorPickingAttachmentForComment,
    addAttachmentForComment,
    setDummyFileUploadProgressForComment,

    // Backward-compatible wrapper functions (deprecated)
    addAttachmentForNewComment,
    addAttachmentForEditedComment,
    uploadFileForNewComment,
    uploadFileForEditedComment,
    setDummyFileUploadProgressForNewComment,
    setDummyFileUploadProgressForEditedComment,

    // Utility
    validateFileType,
  }
}
